import { toAlgoliaFilters, toAlgoliaOptionalFilters } from '@shared/core/search/dialects/algolia';
import { z } from 'zod';

import { postWithFailover } from './failover';
import {
  type MultiSearchHit,
  type MultiSearchLeg,
  type MultiSearchResult,
  type MultiSearchTransport,
  type SearchHighlight,
  type SearchTransport,
} from './types';

const responseSchema = z.object({ hits: z.array(z.unknown()) });

const multiResponseSchema = z.object({
  results: z
    .array(
      z.object({
        hits: z.array(z.record(z.string(), z.unknown())).catch([]),
        nbHits: z.number().catch(0),
        facets: z.record(z.string(), z.record(z.string(), z.number())).catch({}).optional(),
      })
    )
    .catch([]),
});

const rankingInfoSchema = z
  .object({ nbTypos: z.number(), words: z.number(), proximityDistance: z.number(), nbExactWords: z.number() })
  .partial()
  .catch({});

/** The application id in a `https://{APP}-dsn.algolia.net` endpoint, or null when it carries none. */
export const algoliaApplicationId = (endpoint: string): string | null =>
  /^https:\/\/([^./]+)-dsn\./i.exec(endpoint)?.[1] ?? null;

const applicationId = (endpoint: string): string => {
  const appId = algoliaApplicationId(endpoint);
  if (appId === null) throw new Error('Algolia endpoint carries no application id');
  return appId;
};

/** The DSN host first, then the three fallback hosts every Algolia application answers on. */
const hostsOf = (endpoint: string, appId: string): string[] => [
  new URL(endpoint).origin,
  ...[1, 2, 3].map((n) => `https://${appId}-${n}.algolianet.com`),
];

/** Posts to an Algolia path, failing over across the application's hosts. */
async function postAlgolia(
  endpoint: string,
  key: string,
  path: string,
  body: string,
  signal?: AbortSignal
): Promise<unknown> {
  const appId = applicationId(endpoint);
  // Auth travels as query parameters, as Algolia's own browser client sends it: a text/plain POST with no
  // custom header is a "simple" request, so no CORS preflight precedes a search on any host.
  const headers = { 'Content-Type': 'text/plain' };
  return postWithFailover(
    hostsOf(endpoint, appId),
    (host) => {
      const url = new URL(path, host);
      url.searchParams.set('x-algolia-application-id', appId);
      url.searchParams.set('x-algolia-api-key', key);
      return { url, init: { method: 'POST', headers, body } };
    },
    signal,
    'Algolia'
  );
}

const MARK_OPEN = '<mark>';
const MARK_CLOSE = '</mark>';

// Algolia orders hits by typos, then matched words, then proximity, then exact words; one
// number in that order lets hits from different indexes compare.
const scoreOf = (rankingInfo: unknown): number => {
  const { nbTypos = 0, words = 0, proximityDistance = 0, nbExactWords = 0 } = rankingInfoSchema.parse(rankingInfo);
  return -nbTypos * 1e9 + words * 1e6 - Math.min(proximityDistance, 999) * 1e3 + Math.min(nbExactWords, 999);
};

/** The highlighted value of an attribute that matched; an array attribute yields its first matching element. */
const matchedSnippet = (entry: unknown): string | undefined => {
  if (Array.isArray(entry)) return entry.map(matchedSnippet).find((snippet) => snippet !== undefined);
  if (typeof entry !== 'object' || entry === null) return undefined;
  const { value, matchLevel } = entry as { value?: unknown; matchLevel?: unknown };
  return typeof value === 'string' && matchLevel !== undefined && matchLevel !== 'none' ? value : undefined;
};

const highlightOf = (highlightResult: unknown): SearchHighlight => {
  if (typeof highlightResult !== 'object' || highlightResult === null) return {};
  return Object.fromEntries(
    Object.entries(highlightResult).flatMap(([field, entry]) => {
      const snippet = matchedSnippet(entry);
      return snippet === undefined ? [] : [[field, { snippet }]];
    })
  );
};

const toHit = ({
  _highlightResult,
  _rankingInfo,
  _snippetResult,
  ...document
}: Record<string, unknown>): MultiSearchHit => ({
  document,
  score: scoreOf(_rankingInfo),
  highlight: highlightOf(_highlightResult),
});

const legParams = (leg: MultiSearchLeg): string => {
  const params = new URLSearchParams({
    query: leg.query.trim(),
    hitsPerPage: String(leg.hitsPerPage),
    highlightPreTag: MARK_OPEN,
    highlightPostTag: MARK_CLOSE,
    // Snippets render as text nodes, so Algolia must not entity-encode them.
    escapeHTML: 'false',
    getRankingInfo: 'true',
  });
  const filters = toAlgoliaFilters(leg.filter);
  if (filters !== '') params.set('filters', filters);
  if (leg.facets !== undefined && leg.facets.length > 0) params.set('facets', JSON.stringify(leg.facets));
  return params.toString();
};

const MISSING_LEG: MultiSearchResult = { found: 0, hits: [], facetCounts: {}, error: 'Algolia returned no result' };

export function createAlgoliaTransport(endpoint: string, key: string): SearchTransport & MultiSearchTransport {
  return {
    async search<TDoc>({
      descriptor,
      query,
      filter,
      hitsPerPage,
      boosts,
      signal,
    }: Parameters<SearchTransport['search']>[0]) {
      const filters = toAlgoliaFilters(filter);
      const path = `/1/indexes/${encodeURIComponent(descriptor.indexName.algolia)}/query`;

      const params = new URLSearchParams({ query, hitsPerPage: String(hitsPerPage) });
      if (filters !== '') params.set('filters', filters);
      if (boosts !== undefined && boosts.length > 0) {
        params.set('optionalFilters', JSON.stringify(toAlgoliaOptionalFilters(boosts)));
      }

      const response = await postAlgolia(endpoint, key, path, JSON.stringify({ params: params.toString() }), signal);
      return responseSchema.parse(response).hits as TDoc[];
    },

    async multiSearch(legs, signal) {
      if (legs.length === 0) return [];
      const requests = legs.map((leg) => ({ indexName: leg.collection, params: legParams(leg) }));
      const response = await postAlgolia(endpoint, key, '/1/indexes/*/queries', JSON.stringify({ requests }), signal);
      const { results } = multiResponseSchema.parse(response);
      return legs.map((_, index): MultiSearchResult => {
        const result = results[index];
        if (result === undefined) return MISSING_LEG;
        return {
          found: result.nbHits,
          hits: result.hits.map(toHit),
          facetCounts: Object.fromEntries(
            Object.entries(result.facets ?? {}).map(([field, counts]) => [
              field,
              Object.entries(counts)
                .map(([value, count]) => ({ value, count }))
                .sort((a, b) => b.count - a.count),
            ])
          ),
        };
      });
    },
  };
}
